import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';

// A simple, reusable image slider component for the "Before & After" feature
const ImageSlider = ({ beforeImageUrl, afterImageUrl }) => {
    const [showAfter, setShowAfter] = useState(false);

    return (
        <div className="relative w-full aspect-video bg-gray-200 rounded-lg overflow-hidden">
            <AnimatePresence>
                <motion.img
                    key={showAfter ? 'after' : 'before'}
                    src={showAfter ? afterImageUrl : beforeImageUrl}
                    alt={showAfter ? 'After the event' : 'Before the event'}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0 w-full h-full object-cover"
                />
            </AnimatePresence>
            <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md">
                {showAfter ? 'After' : 'Before'}
            </div>
            <button 
                onClick={() => setShowAfter(!showAfter)} 
                className="absolute top-1/2 right-2 -translate-y-1/2 bg-white/70 p-1 rounded-full shadow-md hover:bg-white transition-colors"
            >
                {showAfter ? <ArrowLeft size={20} /> : <ArrowRight size={20} />}
            </button>
        </div>
    );
};


const StoryModal = ({ isOpen, onClose, request }) => {

    const backdropVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
    };

    const modalVariants = {
        hidden: { opacity: 0, y: 50, scale: 0.9 },
        visible: { 
            opacity: 1, 
            y: 0, 
            scale: 1,
            transition: { staggerChildren: 0.2, delayChildren: 0.1 }
        },
        exit: { opacity: 0, y: 50, scale: 0.9 },
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 },
    };

    if (!request) return null;

    const postedDate = new Date(request.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const completedDate = new Date(request.updatedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    variants={backdropVariants}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
                    onClick={onClose}
                >
                    <motion.div
                        variants={modalVariants}
                        className="bg-white rounded-2xl p-6 w-full max-w-md"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="text-center mb-4">
                            <h2 className="text-2xl font-bold text-gray-900">{request.title}</h2>
                            <p className="text-gray-500">Impact Story</p>
                        </div>

                        {/* --- NEW: "Before & After" Image Slider --- */}
                        {request.beforeImageUrl && request.afterImageUrl && (
                            <motion.div variants={itemVariants} className="mb-6">
                                <ImageSlider 
                                    beforeImageUrl={request.beforeImageUrl}
                                    afterImageUrl={request.afterImageUrl}
                                />
                            </motion.div>
                        )}

                        <div className="relative pl-6">
                            <div className="absolute left-8 top-2 bottom-2 w-0.5 bg-gray-200"></div>

                            <motion.div variants={itemVariants} className="relative mb-6">
                                <div className="absolute left-0 top-1.5 w-5 h-5 bg-gray-300 rounded-full z-10"></div>
                                <p className="ml-8 text-sm text-gray-500">{postedDate}</p>
                                <h3 className="ml-8 font-semibold text-gray-800">Request Posted</h3>
                            </motion.div>

                            <motion.div variants={itemVariants} className="relative mb-6">
                                <div className="absolute left-0 top-1.5 w-5 h-5 bg-blue-500 rounded-full z-10"></div>
                                <p className="ml-8 text-sm text-gray-500">You Joined!</p>
                                <h3 className="ml-8 font-semibold text-gray-800">{request.volunteersAccepted} {request.volunteersAccepted > 1 ? 'Volunteers' : 'Volunteer'} Needed</h3>
                            </motion.div>

                            <motion.div variants={itemVariants} className="relative">
                                <div className="absolute left-0 top-1.5 w-5 h-5 bg-green-500 rounded-full z-10"></div>
                                <p className="ml-8 text-sm text-gray-500">{completedDate}</p>
                                <h3 className="ml-8 font-semibold text-gray-800">Task Completed!</h3>
                                <p className="ml-8 text-gray-600">You earned {request.points} points and {request.impactPointsValue} RP.</p>
                            </motion.div>
                        </div>
                        <button onClick={onClose} className="mt-6 w-full py-3 bg-black text-white rounded-lg font-semibold">
                            Close
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default StoryModal;
